(function () {
  if (!document.body.classList.contains('template-product')) return;

  window.setTimeout(() => {
    document.body.classList.remove('zb-product-preflight');
  }, 3500);

  const COLOR_WORDS = ['color', 'renk', 'colour', 'farbe', 'couleur'];
  const SIZE_WORDS = ['size', 'beden', 'numara', 'grosse', 'taille'];
  // TR option values, the sticky bar and the cart line are EU sizes, so EU leads
  // and is the unit the chips open in.
  const SIZE_UNITS = ['EU', 'US', 'UK'];

  const state = {
    product: null,
    colorOption: null,
    sizeOption: null,
    groups: new Map(),
    activeUnit: 'EU',
    previewColor: '',
    committingColor: '',
    queued: 0,
  };

  const normalize = (value) =>
    String(value || '')
      .toLocaleLowerCase('tr')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]+/g, '');

  const imageKey = (url) => {
    const clean = String(url || '').split('?')[0].split('#')[0];
    const name = clean
      .slice(clean.lastIndexOf('/') + 1)
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.[a-z0-9]+$)/i, '');
    try {
      return decodeURIComponent(name).toLocaleLowerCase('tr');
    } catch (_error) {
      return name.toLocaleLowerCase('tr');
    }
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const preloadedImages = new Set();

  const preloadImage = (url, width) => {
    const key = imageKey(url);
    if (!key || preloadedImages.has(key)) return;
    preloadedImages.add(key);
    const image = new Image();
    image.decoding = 'async';
    image.src = sizedImage(url, width || 1400);
  };

  // EU -> US/UK/JP shoe-size map, mirroring zerbay.co's "US6-EU39-UK5" variant
  // data, so plain-EU TR variants (e.g. "40") still convert across US/EU/UK/JP.
  // JP = EU - 14, matching zerbay.co's size-guide JP column (EU39->JP25 ... EU48->JP34).
  const EU_SIZE_TABLE = {
    '38': { US: '5', EU: '38', UK: '4', JP: '24' },
    '39': { US: '6', EU: '39', UK: '5', JP: '25' },
    '40': { US: '7', EU: '40', UK: '6', JP: '26' },
    '41': { US: '8', EU: '41', UK: '7', JP: '27' },
    '42': { US: '9', EU: '42', UK: '8', JP: '28' },
    '43': { US: '10', EU: '43', UK: '9', JP: '29' },
    '44': { US: '11', EU: '44', UK: '10', JP: '30' },
    '45': { US: '12', EU: '45', UK: '11', JP: '31' },
    '46': { US: '13', EU: '46', UK: '12', JP: '32' },
    '47': { US: '14', EU: '47', UK: '13', JP: '33' },
    '48': { US: '15', EU: '48', UK: '14', JP: '34' },
  };

  const parseSize = (value) => {
    const parts = {};
    String(value || '').replace(/\b(US|EU|UK|JP)\s*([0-9.]+)/gi, (_match, unit, size) => {
      parts[unit.toUpperCase()] = size;
      return '';
    });
    if (!parts.US && !parts.EU && !parts.UK) {
      const eu = String(value || '').trim().replace(/[^0-9.]/g, '');
      if (EU_SIZE_TABLE[eu]) return { ...EU_SIZE_TABLE[eu] };
    }
    return parts;
  };

  const optionMatches = (option, words) => {
    const name = normalize(option && option.name);
    return words.some((word) => name === normalize(word) || name.includes(normalize(word)));
  };

  // The words come first; for an option name none of them matches, the option position the theme rendered
  // on the picker (data-zb-color-index / data-zb-size-index, 0-based: by name, else colour = 1st, size = 2nd).
  const zbOptionIndex = (words) => {
    const picker = document.querySelector('variant-picker[data-product-url]');
    const value = picker && picker.getAttribute(words === COLOR_WORDS ? 'data-zb-color-index' : 'data-zb-size-index');
    const index = value === null || value === '' ? -1 : Number(value);
    return Number.isInteger(index) ? index : -1;
  };

  const findProductOption = (product, words) => {
    const options = product && product.options ? product.options : [];
    const index = zbOptionIndex(words);
    return (
      options.find((option) => optionMatches(option, words)) ||
      (index >= 0 ? options.find((option) => option.position === index + 1) : undefined)
    );
  };

  const selectedInput = (fieldset) =>
    fieldset && (fieldset.querySelector('input[type="radio"]:checked') || fieldset.querySelector('[data-current-checked="true"]'));

  const selectedValue = (fieldset) => {
    const input = selectedInput(fieldset);
    return input ? input.value : '';
  };

  const fieldsetByOption = (picker, option, words) => {
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    if (option && option.position) {
      const byIndex = fieldsets.find((fieldset) => Number(fieldset.dataset.fieldsetIndex) === option.position - 1);
      if (byIndex) return byIndex;
    }

    const index = zbOptionIndex(words);
    return (
      fieldsets.find((fieldset) => {
        const text = [
          fieldset.querySelector('legend')?.textContent,
          ...Array.from(fieldset.querySelectorAll('input')).map((input) => input.name),
        ].join(' ');
        const normalized = normalize(text);
        return words.some((word) => normalized.includes(normalize(word)));
      }) || (index >= 0 ? fieldsets.find((fieldset) => Number(fieldset.dataset.fieldsetIndex) === index) : undefined)
    );
  };

  const setLegend = (fieldset, label, value) => {
    const legend = fieldset && fieldset.querySelector('legend');
    if (!legend) return;
    let current = legend.querySelector('.zb-option-current');
    if (!current) {
      // The markup's legend already carries the option's own name - "Renk" and
      // "Beden" here - so keep that rather than the English label passed in.
      // Only the legend's own text counts; a swatch style also renders the
      // selected value inside an element of its own.
      const rendered = [...legend.childNodes]
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join('')
        .replace(/:\s*$/, '')
        .trim();
      legend.textContent = '';
      legend.append(document.createTextNode(`${rendered || label}: `));
      current = document.createElement('span');
      current.className = 'zb-option-current';
      legend.append(current);
    }
    const next = value || '';
    if (current.textContent !== next) current.textContent = next;
  };

  const FILE_CODES = ['ll', 'br', 'zr', 'ls', 'rk'];

  // s3/43: a colour with no photo (made on request) gets a filled chip instead of a plain name tile: [fill, light text?],
  // keyed by the colour's file token. Other colours keep the plain name tile.
  const ZB_CHIPS = { tan: ['#B07A4A', false], green: ['#3E5B3A', true] };

  // Media filenames identify colors in English while the option values are Turkish.
  // The table is rendered from snippets/zerbay-color-file-tokens.liquid.
  const colorFileTokens = (() => {
    const table = new Map();
    String(window.ZB_COLOR_FILE_TOKENS || '')
      .split(',')
      .forEach((pair) => {
        const [value, token] = pair.split(':');
        if (value && token) table.set(normalize(value), token.trim().toLowerCase());
      });
    return table;
  })();

  const fileTokenFor = (color) => colorFileTokens.get(normalize(color)) || '';

  const fileMatchesToken = (src, token) =>
    Boolean(token) && FILE_CODES.some((code) => String(src || '').toLowerCase().includes(`-${token}-${code}`));

  // Group media by the color named in each filename. Splitting the media evenly
  // across the colors instead shifts later colors whenever extra files are
  // appended - restored variant images land after the normal color groups, so the
  // last color ends up with two photos that belong to another color.
  // Returns null when the filenames cannot answer for every color, and the caller
  // falls back to even splitting.
  const groupImagesByFilename = (colors, images) => {
    const tokens = colors.map(fileTokenFor);
    if (tokens.some((token) => !token)) return null;

    const groups = colors.map(() => []);
    images.forEach((image) => {
      let claimant = -1;
      tokens.forEach((token, index) => {
        if (!fileMatchesToken(image.src, token)) return;
        // A shorter token is a suffix of a longer one ('cream' also matches the
        // 'navy-cream' files), so the most specific color claims the file.
        if (claimant < 0 || token.length > tokens[claimant].length) claimant = index;
      });
      if (claimant >= 0) groups[claimant].push(image);
    });

    return groups.every((group) => group.length) ? groups : null;
  };

  const buildGroups = () => {
    state.groups = new Map();
    // s2/27: the tile's own-file counts belong to the product data being grouped now
    state.zbOwnFiles = null;
    state.colorOption = findProductOption(state.product, COLOR_WORDS);
    state.sizeOption = findProductOption(state.product, SIZE_WORDS);

    const colors = state.colorOption && state.colorOption.values ? state.colorOption.values : [];
    const images = state.product && state.product.images ? state.product.images : [];
    if (!colors.length || !images.length) return;

    const filenameGroups = groupImagesByFilename(colors, images);
    const base = Math.floor(images.length / colors.length);
    const remainder = images.length % colors.length;
    let start = 0;

    colors.forEach((color, index) => {
      let colorImages;
      if (filenameGroups) {
        colorImages = filenameGroups[index];
      } else {
        const count = base + (index < remainder ? 1 : 0);
        colorImages = images.slice(start, start + count);
        start += count;
      }

      state.groups.set(normalize(color), {
        color,
        index,
        images: colorImages,
        galleryImages: colorImages.length > 4 ? colorImages.slice(1, -4).concat(colorImages.slice(0, 1)) : colorImages,
      });
    });
  };

  const groupForColor = (color) => state.groups.get(normalize(color));

  const canPreviewOnHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const inputsInFieldset = (fieldset) => Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []);

  const colorIndexForInput = (input, fieldset) => inputsInFieldset(fieldset).indexOf(input);

  const previewGlobalGalleryColor = (color, colorIndex) => {
    const resolvedIndex =
      Number.isFinite(colorIndex) && colorIndex >= 0
        ? colorIndex
        : (state.colorOption?.values || []).findIndex((value) => normalize(value) === normalize(color));

    if (resolvedIndex < 0) return;

    if (typeof window.galleryShowColorIndex === 'function') {
      window.galleryShowColorIndex(resolvedIndex);
    } else {
      document.dispatchEvent(
        new CustomEvent('gallery:preview', {
          detail: { colorValue: color, colorIndex: resolvedIndex },
        })
      );
    }
  };

  const showPreviewColor = (color, colorIndex) => {
    if (!canPreviewOnHover() || !color) return;
    state.previewColor = color;
    previewGlobalGalleryColor(color, colorIndex);
    filterGallery();
  };

  const clearPreviewColor = (fieldset) => {
    if (state.committingColor) return;
    if (!state.previewColor) return;
    state.previewColor = '';
    const input = selectedInput(fieldset);
    if (input) previewGlobalGalleryColor(input.value, colorIndexForInput(input, fieldset));
    filterGallery();
  };

  const commitPreviewColor = (input, fieldset) => {
    if (!input || !fieldset) return;

    const colorIndex = colorIndexForInput(input, fieldset);
    state.committingColor = input.value;
    state.previewColor = input.value;
    setLegend(fieldset, 'Color', input.value);
    previewGlobalGalleryColor(input.value, colorIndex);
    filterGallery();
  };

  const loadProduct = async () => {
    if (state.product) return state.product;

    const picker = document.querySelector('variant-picker[data-product-url]');
    const productUrl = picker?.dataset.productUrl || window.location.pathname.replace(/\/$/, '');

    try {
      const response = await fetch(`${productUrl}.json`, { credentials: 'same-origin' });
      const data = response.ok ? await response.json() : null;
      state.product = data && data.product ? data.product : { options: [], images: [], variants: [] };
    } catch (_error) {
      state.product = { options: [], images: [], variants: [] };
    }

    buildGroups();
    return state.product;
  };

  const decorateHeader = () => {
    const details = document.querySelector('.product-details');
    const heading = details && details.querySelector('h1');
    if (!details || !heading) return;

    details.classList.add('zb-product-details-ready');
    const headerGroup = heading.closest('.group-block');
    const headerContent = headerGroup && headerGroup.querySelector('.group-block-content');
    headerGroup?.classList.add('zb-product-heading-group');
    headerContent?.classList.add('zb-product-heading-content');
  };

  const decorateLayout = () => {
    document.querySelector('[data-testid="product-information"]')?.classList.add('product-area');
    document.querySelector('[data-testid="product-information-media"]')?.classList.add('product-gallery');
    document.querySelector('.product-details')?.classList.add('product-details-wrapper');
    document.querySelector('.zb-product-heading-content')?.classList.add('product__title-price-container');
  };

  // s2/27 (A1, plan 2.6): the colour tile shows (1) the image of a variant of that colour (the square 9th photo, the
  // cross-angle pair, on 208/211 colour blocks), else (2) today's first photo of the colour's group, else (3) a plain name
  // tile (owner Q4) for a colour that has no photo of its own (no variant image, no file with its filename token) while
  // other colours of the product have their files - today's even split would show another colour's photo there.
  // When no colour has its own files (non-standard filenames) every tile keeps today's photo.
  const zbTileSource = (color) => {
    const product = state.product || {};
    const images = product.images || [];
    const colorIndex = zbColorIndex();
    const value = normalize(color);
    let variantImage = null;
    if (colorIndex >= 0) {
      (product.variants || []).some((variant) => {
        const options = variant.options || [variant.option1, variant.option2, variant.option3];
        if (normalize(options[colorIndex]) !== value) return false;
        // product .json: variants[].image_id; products.json-style data: featured_image / images[].variant_ids
        const imageId = variant.image_id || (variant.featured_image && variant.featured_image.id);
        if (imageId) variantImage = images.find((image) => String(image.id) === String(imageId)) || null;
        else variantImage = images.find((image) => (image.variant_ids || []).some((id) => String(id) === String(variant.id))) || null;
        return Boolean(variantImage);
      });
    }
    if (variantImage) return { image: variantImage, name: false };
    if (!state.zbOwnFiles) {
      // own files per colour, the same claim rule as groupImagesByFilename (the longest matching token wins), without
      // its "every colour has files" condition
      const colors = (state.colorOption && state.colorOption.values) || [];
      const tokens = colors.map(fileTokenFor);
      const counts = new Map(colors.map((c) => [normalize(c), 0]));
      const firsts = new Map();
      images.forEach((image) => {
        let claimant = -1;
        tokens.forEach((token, index) => {
          if (!fileMatchesToken(image.src, token)) return;
          if (claimant < 0 || token.length > tokens[claimant].length) claimant = index;
        });
        if (claimant < 0) return;
        const key = normalize(colors[claimant]);
        counts.set(key, counts.get(key) + 1);
        if (!firsts.has(key)) firsts.set(key, image);
      });
      state.zbOwnFiles = { counts, firsts, any: [...counts.values()].some((n) => n > 0) };
    }
    const own = state.zbOwnFiles.counts.get(value) || 0;
    if (!own && state.zbOwnFiles.any) return { image: null, name: true };
    // the colour's own first file (buildGroups falls back to an even split when any colour has no files)
    if (own) return { image: state.zbOwnFiles.firsts.get(value) || null, name: false };
    const group = groupForColor(color);
    return { image: (group && group.images[0]) || null, name: false };
  };

  const decorateColors = (picker, fieldset) => {
    if (!fieldset) return;
    fieldset.classList.add('zb-product-color-fieldset');
    setLegend(fieldset, 'Color', state.previewColor || selectedValue(fieldset));

    Array.from(fieldset.querySelectorAll('label')).forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;

      const tile = zbTileSource(input.value);
      const image = tile.image;
      label.classList.add('zb-product-color-label');
      label.dataset.zbColor = normalize(input.value);
      label.title = input.value;
      if (!label.dataset.zbPreviewBound) {
        label.dataset.zbPreviewBound = 'true';
        // Not on a touch pointerdown: a finger that lands on a swatch and then scrolls the page never selects it
        // (no click follows), yet it switched the gallery and legend to that colour. A real tap still commits on
        // the mousedown and click that iOS/Android send when the finger lifts.
        label.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch') commitPreviewColor(input, fieldset);
        });
        label.addEventListener('mousedown', () => commitPreviewColor(input, fieldset));
        label.addEventListener('click', () => commitPreviewColor(input, fieldset));
        label.addEventListener('mouseenter', () => {
          const colorIndex = colorIndexForInput(input, fieldset);
          setLegend(fieldset, 'Color', input.value);
          showPreviewColor(input.value, colorIndex);
        });
        label.addEventListener('focusin', () => {
          const colorIndex = colorIndexForInput(input, fieldset);
          setLegend(fieldset, 'Color', input.value);
          showPreviewColor(input.value, colorIndex);
        });
        label.addEventListener('focusout', () => {
          setLegend(fieldset, 'Color', selectedValue(fieldset));
          clearPreviewColor(fieldset);
        });
      }

      let thumb = label.querySelector('.zb-product-swatch-thumb');
      if (!thumb) {
        thumb = document.createElement('span');
        thumb.className = 'zb-product-swatch-thumb';
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = '';
        thumb.append(img);
        input.insertAdjacentElement('afterend', thumb);
      }

      const img = thumb.querySelector('img');
      if (image && img && img.dataset.zbKey !== imageKey(image.src)) {
        img.dataset.zbKey = imageKey(image.src);
        img.src = sizedImage(image.src, 180);
      }
      // s2/27 name tile (owner Q4): the colour value, hidden from assistive tech (the label already names the colour)
      let nameTile = thumb.querySelector('.zb-tile-name');
      if (tile.name) {
        if (!nameTile) {
          nameTile = document.createElement('span');
          nameTile.className = 'zb-tile-name';
          nameTile.setAttribute('aria-hidden', 'true');
          thumb.append(nameTile);
        }
        if (nameTile.textContent !== input.value) nameTile.textContent = input.value;
        const chip = ZB_CHIPS[fileTokenFor(input.value)];
        nameTile.classList.toggle('zb-tile-name--chip', Boolean(chip));
        nameTile.classList.toggle('zb-tile-name--light', Boolean(chip && chip[1]));
        if (chip) nameTile.style.setProperty('--zb-chip', chip[0]);
        else nameTile.style.removeProperty('--zb-chip');
        if (img && !img.hidden) img.hidden = true;
      } else {
        if (nameTile) nameTile.remove();
        if (img && img.hidden) img.hidden = false;
      }

      label.classList.toggle('zb-is-selected', input.checked);
    });

    if (!fieldset.dataset.zbPreviewLeaveBound) {
      fieldset.dataset.zbPreviewLeaveBound = 'true';
      fieldset.addEventListener('mouseleave', () => {
        setLegend(fieldset, 'Color', selectedValue(fieldset));
        clearPreviewColor(fieldset);
      });
    }
  };

  // s2/23 (B26): the size guide is a native modal dialog built from the <template data-zb-size-guide> that
  // snippets/variant-main-picker.liquid prints next to the trigger (texts from tr.json and product data; no literals
  // here). showModal: the page behind is inert, Tab stays inside, Escape closes. On open the stock cells are filled for
  // the selected colour and the chosen size's row is marked; focus goes to the close button and back to the trigger on
  // close (button, Escape, backdrop); the page scroll is locked while open. Rows come from the size values (EU, US, UK,
  // JP). Without the template no dialog is built and the link opens /pages/size-guide.
  const SIZE_GUIDE_ID = 'zb-size-guide-dialog';

  const sizeGuideStatusByEu = () => {
    const map = ensureStockMap();
    const picker = document.querySelector('variant-picker');
    if (!map || !picker) return null;
    const colorIndex = zbColorIndex();
    const sizeIndex = zbSizeIndex();
    if (sizeIndex < 0) return null;
    const selectedColor = zbSelectedColor(fieldsetByOption(picker, state.colorOption, COLOR_WORDS));
    if (colorIndex >= 0 && !selectedColor) return null;
    const priority = { in_stock: 3, continue_selling: 2, stop_selling: 1 };
    const byEu = {};
    Object.keys(map).forEach((id) => {
      const entry = map[id];
      const options = entry && entry.o ? entry.o : [];
      if (colorIndex >= 0 && normalize(options[colorIndex]) !== selectedColor) return;
      const eu = parseSize(options[sizeIndex]).EU;
      if (!eu) return;
      const status = entry.s || (entry.a ? 'in_stock' : 'stop_selling');
      if (!byEu[eu] || (priority[status] || 0) > (priority[byEu[eu]] || 0)) byEu[eu] = status;
    });
    return byEu;
  };

  const fillSizeGuide = (dialog) => {
    if (!dialog) return;
    const word = dialog.dataset.zbSgSoldout || '';
    const byEu = sizeGuideStatusByEu() || {};
    const picker = document.querySelector('variant-picker');
    const sizeFieldset = picker && fieldsetByOption(picker, state.sizeOption, SIZE_WORDS);
    // While the s2/22 gate says no size is chosen, no row is marked.
    const chosen = sizeFieldset && !zbNoSizeChosen() ? parseSize(selectedValue(sizeFieldset)).EU || '' : '';
    dialog.querySelectorAll('[data-zb-sg-eu]').forEach((cell) => {
      const status = byEu[cell.dataset.zbSgEu] || '';
      const text = status && status !== 'in_stock' ? word : '';
      if (cell.textContent !== text) cell.textContent = text;
    });
    dialog.querySelectorAll('tr[data-zb-sg-row]').forEach((row) => {
      const isChosen = chosen !== '' && row.dataset.zbSgRow === chosen;
      if (row.classList.contains('zb-sg__row--chosen') !== isChosen) row.classList.toggle('zb-sg__row--chosen', isChosen);
    });
  };

  // The page scroll lock follows the guide: on while it is open and on the page, off otherwise (also when it leaves the
  // page without a close event; the body MutationObserver runs enhance(), which calls this).
  const syncSizeGuideLock = () => {
    const dialog = document.getElementById(SIZE_GUIDE_ID);
    const isOpen = Boolean(dialog && dialog.open && dialog.isConnected);
    if (document.documentElement.classList.contains('zb-sg-lock') !== isOpen) {
      document.documentElement.classList.toggle('zb-sg-lock', isOpen);
    }
  };

  const closeSizeGuide = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      if (dialog.open) dialog.close();
      return;
    }
    dialog.removeAttribute('open');
    dialog.dispatchEvent(new Event('close'));
  };

  const ensureSizeDialog = (values) => {
    let dialog = document.getElementById(SIZE_GUIDE_ID);
    if (!dialog) {
      const template = document.querySelector('variant-picker template[data-zb-size-guide]');
      if (!template) return null;
      dialog = document.createElement('dialog');
      dialog.id = SIZE_GUIDE_ID;
      dialog.className = 'zb-sg';
      dialog.setAttribute('aria-labelledby', 'zbSizeGuideTitle');
      dialog.dataset.zbSgSoldout = template.dataset.zbSgSoldout || '';
      dialog.append(template.content.cloneNode(true));
      document.body.append(dialog);
      // Backdrop = press and release both on the dialog element outside its box (a text selection dragged out of the
      // sheet does not close it).
      dialog.addEventListener('pointerdown', (event) => {
        dialog.zbDown = event.target === dialog;
      });
      dialog.addEventListener('click', (event) => {
        if (event.target.closest && event.target.closest('.zb-sg__close')) {
          closeSizeGuide(dialog);
          return;
        }
        const down = dialog.zbDown !== false;
        dialog.zbDown = null;
        if (event.target !== dialog || !down) return;
        const r = dialog.getBoundingClientRect();
        const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
        if (!inside) closeSizeGuide(dialog);
      });
      dialog.addEventListener('close', () => {
        syncSizeGuideLock();
        // A close event that arrives after the guide was opened again changes nothing else.
        if (dialog.open) return;
        const trigger = dialog.zbTrigger && dialog.zbTrigger.isConnected ? dialog.zbTrigger : document.querySelector('.zb-size-guide-button');
        dialog.zbTrigger = null;
        if (trigger && typeof trigger.focus === 'function') {
          try {
            trigger.focus({ preventScroll: true });
          } catch (_error) {
            trigger.focus();
          }
        }
      });
    }

    const body = dialog.querySelector('tbody');
    if (body && body.dataset.zbValues !== values.join('|')) {
      body.dataset.zbValues = values.join('|');
      body.textContent = '';
      values.forEach((value) => {
        const size = parseSize(value);
        const row = document.createElement('tr');
        if (size.EU) row.dataset.zbSgRow = size.EU;
        const head = document.createElement('th');
        head.scope = 'row';
        head.textContent = size.EU || '-';
        row.append(head);
        [size.US, size.UK, size.JP].forEach((part) => {
          const cell = document.createElement('td');
          cell.textContent = part || '-';
          row.append(cell);
        });
        const stock = document.createElement('td');
        stock.className = 'zb-sg__stock';
        if (size.EU) stock.dataset.zbSgEu = size.EU;
        row.append(stock);
        body.append(row);
      });
    }

    return dialog;
  };

  const openSizeGuide = (trigger, values) => {
    const dialog = ensureSizeDialog(values);
    if (!dialog) return false;
    if (dialog.open) return true;
    fillSizeGuide(dialog);
    dialog.zbTrigger = trigger;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    syncSizeGuideLock();
    const close = dialog.querySelector('.zb-sg__close');
    if (close) {
      try {
        close.focus({ preventScroll: true });
      } catch (_error) {
        close.focus();
      }
    }
    return true;
  };

  const updateSizeLabels = (fieldset) => {
    if (!fieldset) return;
    const selected = selectedValue(fieldset);
    // The legend keeps the option's own name (server markup); the fallback is the option name from the product JSON.
    setLegend(fieldset, (state.sizeOption && state.sizeOption.name) || '', selected);
    // s2/22: on the live product picker (data-zb-pdp-live, set by the gate script in blocks/buy-buttons.liquid) a chip
    // is named by its size only; "Tükendi" comes once, from the sold-out description (aria-describedby).
    const isLivePicker = Boolean(fieldset.closest('variant-picker[data-zb-pdp-live]'));

    Array.from(fieldset.querySelectorAll('label')).forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      const text = label.querySelector('[data-key="variant-option-text"]') || label.querySelector('.variant-option__button-label__text');
      if (!input || !text) return;

      const parsed = parseSize(input.value);
      const visibleSize = parsed[state.activeUnit] || input.value;
      if (text.textContent !== visibleSize) text.textContent = visibleSize;
      // The server's aria-label ('39', '39 - Tükendi') names the EU size. When the
      // chip shows another unit, name both so a screen reader matches the chip.
      if (!input.dataset.zbAria) input.dataset.zbAria = input.getAttribute('aria-label') || input.value;
      const baseAria = isLivePicker ? input.value : input.dataset.zbAria;
      const aria =
        state.activeUnit === 'EU' || !parsed.EU || !parsed[state.activeUnit]
          ? baseAria
          : baseAria.replace(input.value, `${state.activeUnit} ${visibleSize} (EU ${parsed.EU})`);
      if (input.getAttribute('aria-label') !== aria) input.setAttribute('aria-label', aria);
      label.dataset.zbSizeRaw = input.value;
      label.classList.toggle('zb-is-selected', input.checked);
      SIZE_UNITS.forEach((unit) => {
        if (parsed[unit]) label.dataset[`zbSize${unit}`] = parsed[unit];
      });
    });
  };

  const decorateSizes = (fieldset) => {
    if (!fieldset) return;
    fieldset.classList.add('zb-product-size-fieldset');

    const values =
      state.sizeOption && state.sizeOption.values
        ? state.sizeOption.values
        : Array.from(fieldset.querySelectorAll('input[type="radio"]')).map((input) => input.value);

    // snippets/variant-main-picker.liquid prints this row on every product page (server HTML, for crawlers without
    // JS). s2/22: the JS fallback copy with its Turkish literals was removed; without the row only the labels update.
    const tools = fieldset.previousElementSibling;
    if (!tools || !tools.classList.contains('zb-size-toolrow')) {
      updateSizeLabels(fieldset);
      return;
    }

    const tabs = tools.querySelector('.zb-size-unit-tabs');
    if (tabs && !tabs.children.length) {
      SIZE_UNITS.forEach((unit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zb-size-unit-tab';
        button.dataset.zbUnit = unit;
        button.textContent = unit;
        tabs.append(button);
      });
    }

    tools.querySelectorAll('.zb-size-unit-tab').forEach((button) => {
      const isActive = button.dataset.zbUnit === state.activeUnit;
      if (button.getAttribute('aria-pressed') !== String(isActive)) {
        button.setAttribute('aria-pressed', String(isActive));
      }
      if (!button.dataset.zbBound) {
        button.dataset.zbBound = 'true';
        button.addEventListener('click', () => {
          state.activeUnit = button.dataset.zbUnit || 'EU';
          scheduleEnhance();
        });
      }
    });

    // Name the unit the chips are in. The equality check matters: the body
    // MutationObserver watches childList, so an unconditional write would loop.
    const sizeLabel = tools.querySelector('.zb-size-label');
    const sizeLabelText = `Numara (${state.activeUnit})`;
    if (sizeLabel && sizeLabel.textContent !== sizeLabelText) sizeLabel.textContent = sizeLabelText;

    const guideButton = tools.querySelector('.zb-size-guide-button');
    // The server row's guide is a link to /pages/size-guide: with JS it opens the dialog instead. The flag is a
    // property, not a data attribute: the variant picker's morph keeps this node but drops attributes the
    // server markup doesn't have, and a second listener would call showModal() on an open dialog.
    if (guideButton && !guideButton.zbGuideBound) {
      guideButton.zbGuideBound = true;
      guideButton.addEventListener('click', (event) => {
        // s2/23: a modified click (new tab / window) and a click without the dialog template open the size guide page.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
        if (openSizeGuide(guideButton, values)) event.preventDefault();
      });
    }
    // s2/23: the link opens a dialog (state attribute, no text; re-set after a picker morph drops it).
    if (guideButton && guideButton.getAttribute('aria-haspopup') !== 'dialog' && document.querySelector('variant-picker template[data-zb-size-guide]')) {
      guideButton.setAttribute('aria-haspopup', 'dialog');
    }

    updateSizeLabels(fieldset);
  };

  const decoratePicker = () => {
    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    picker.classList.add('zb-product-parity-ready');
    const colorFieldset = fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    const sizeFieldset = fieldsetByOption(picker, state.sizeOption, SIZE_WORDS);

    decorateColors(picker, colorFieldset);
    decorateSizes(sizeFieldset);
  };

  const currentProductForm = () => document.querySelector('product-form-component');

  // s2/22 (owner B20): the main product form component carries data-zb-size-chosen (server markup, inside a
  // .shopify-section). While it says "false" no size has been chosen: no sticky bar, no delivery line.
  const zbMainForm = () => document.querySelector('.shopify-section product-form-component[data-zb-size-chosen]');
  const zbNoSizeChosen = () => {
    const form = zbMainForm();
    return Boolean(form) && form.getAttribute('data-zb-size-chosen') === 'false';
  };

  // Option positions (0-based) for the stock map: from the product JSON, else the picker's s2/02 index.
  const zbColorIndex = () =>
    state.colorOption && state.colorOption.position ? state.colorOption.position - 1 : zbOptionIndex(COLOR_WORDS);
  const zbSizeIndex = () =>
    state.sizeOption && state.sizeOption.position ? state.sizeOption.position - 1 : zbOptionIndex(SIZE_WORDS);

  // s2/22 (Codex r5 M2): the selected colour, normalized. A colour option with one value is printed without a
  // fieldset (variant-main-picker.liquid); then the product's only colour value is the selected one (from the
  // product JSON, else from the stock map, where every entry has the same value at the colour index).
  const zbSelectedColor = (colorFieldset) => {
    if (colorFieldset) return normalize(selectedValue(colorFieldset));
    const values = state.colorOption && state.colorOption.values;
    if (values && values.length === 1) return normalize(values[0]);
    const map = ensureStockMap();
    const colorIndex = zbColorIndex();
    if (!map || colorIndex < 0) return null;
    const found = new Set(Object.keys(map).map((id) => normalize(((map[id] && map[id].o) || [])[colorIndex])));
    return found.size === 1 ? [...found][0] : null;
  };

  const getStockState = () => {
    const form = currentProductForm();
    return {
      form,
      status: form?.dataset.zbStockStatus || '',
      canAddToCart: form?.dataset.zbCanAddToCart === 'true',
      variantId: form?.dataset.zbVariantId || '',
    };
  };

  const syncPurchaseShippingText = () => {
    const { status } = getStockState();
    const shipping = document.querySelector('[data-zb-purchase-shipping]');
    if (!shipping) return;
    // s2/22: no delivery line before a size is chosen.
    if (zbNoSizeChosen()) {
      if (!shipping.hidden) shipping.hidden = true;
      return;
    }
    // Without a stock status keep what the server rendered.
    if (!status) return;

    // The text comes from tr.json; only pairs in stock show it. Haber Ver
    // (continue_selling) and sold-out (stop_selling) sizes hide the line.
    const hidden = status !== 'in_stock';
    if (shipping.hidden !== hidden) shipping.hidden = hidden;
  };

  // s2/24 (B22, plan §2.4): the element the sticky bar watches. One attribute, moved here; the bar re-observes on
  // zb:bar-target. Kept across calls so a replaced Notify block (syncStockNotice) is noticed.
  let zbBarTarget = null;
  const zbSetBarTarget = (el) => {
    if (zbBarTarget === el && (!el || el.hasAttribute('data-zb-bar-target'))) return;
    document.querySelectorAll('[data-zb-bar-target]').forEach((node) => {
      if (node !== el) node.removeAttribute('data-zb-bar-target');
    });
    zbBarTarget = el;
    if (el) el.setAttribute('data-zb-bar-target', '');
    document.dispatchEvent(new CustomEvent('zb:bar-target'));
  };

  const syncStickyAvailability = () => {
    // s2/22: the bar is not addable before a size is chosen.
    const { status, canAddToCart: formCanAdd } = getStockState();
    const noSize = zbNoSizeChosen();
    const canAddToCart = formCanAdd && !noSize;
    // s2/24: mode from the size state and the stock status: "buy" (in stock and addable), "notify" (continue_selling),
    // "none" (no size chosen, stop_selling, unknown). Watched element: the main add button, or the live Notify block.
    const form = currentProductForm();
    let mode = 'none';
    if (!noSize && status === 'in_stock' && canAddToCart) mode = 'buy';
    else if (!noSize && status === 'continue_selling') mode = 'notify';
    const mainButton = form ? form.querySelector('[ref="addToCartButton"]') : null;
    const notice = document.querySelector('.shopify-section [data-zb-stock-notify]');
    zbSetBarTarget(mode === 'buy' ? mainButton : mode === 'notify' ? notice : null);
    document.querySelectorAll('sticky-add-to-cart').forEach((sticky) => {
      if (!(sticky instanceof HTMLElement)) return;
      const canAdd = canAddToCart ? 'true' : 'false';
      sticky.dataset.zbCanAddToCart = canAdd;
      sticky.dataset.variantAvailable = canAdd;
      if (sticky.dataset.zbStickyMode !== mode) sticky.dataset.zbStickyMode = mode;
      const button = sticky.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement) button.disabled = mode === 'none';
      const bar = sticky.querySelector('[ref="stickyBar"]');
      if (mode === 'none' && bar instanceof HTMLElement) bar.dataset.stuck = 'false';
    });
    document.dispatchEvent(new CustomEvent('zb:bar-sync'));
  };

  const syncSizeStockUi = () => {
    const { status, canAddToCart } = getStockState();
    const picker = document.querySelector('variant-picker');
    const sizeFieldset = picker && fieldsetByOption(picker, state.sizeOption, SIZE_WORDS);
    if (!sizeFieldset) return;

    sizeFieldset.dataset.zbStockStatus = status;
    sizeFieldset.dataset.zbCanAddToCart = canAddToCart ? 'true' : 'false';

    sizeFieldset.querySelectorAll('label').forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (!(input instanceof HTMLInputElement)) return;

      const optionStatus = input.dataset.zbStockStatus || label.dataset.zbStockStatus || '';
      const isUnavailable = optionStatus
        ? optionStatus === 'stop_selling'
        : input.dataset.optionAvailable === 'false' ||
          input.getAttribute('aria-disabled') === 'true' ||
          input.disabled;
      const isSelected = input.checked;

      label.classList.toggle('zb-is-continue-selling', optionStatus === 'continue_selling');
      label.classList.toggle('zb-is-stop-selling', optionStatus === 'stop_selling');
      label.classList.toggle('zb-is-unavailable', isUnavailable);
      label.classList.toggle('zb-is-selected', isSelected);
      label.classList.toggle('zb-is-selected-addable', isSelected && canAddToCart);
      label.classList.toggle('zb-is-selected-not-addable', isSelected && !canAddToCart);
    });
  };

  const showNotifySuccess = (notice) => {
    if (!notice) return;
    const success = notice.querySelector('[data-zb-stock-notify-success]');
    const row = notice.querySelector('.zb-stock-notify__row');
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.classList.remove('hidden');
    }
    if (row instanceof HTMLElement) row.hidden = true;
    notice.classList.add('zb-stock-notify--sent');
  };

  const bindNotifyFeedback = () => {
    const submittedPath = (() => {
      try {
        return sessionStorage.getItem('zbStockNotifySubmitted');
      } catch (_error) {
        return '';
      }
    })();

    document.querySelectorAll('[data-zb-stock-notify]').forEach((notice) => {
      if (!(notice instanceof HTMLElement)) return;

      const key = `${location.pathname}::${notice.dataset.zbNotifyKey || ''}`;
      const form = notice.querySelector('form');
      const shouldShowSuccess =
        submittedPath === key || new URLSearchParams(location.search).get('contact_posted') === 'true';

      if (shouldShowSuccess) {
        showNotifySuccess(notice);
        try {
          sessionStorage.removeItem('zbStockNotifySubmitted');
        } catch (_error) {}
      }

      if (!(form instanceof HTMLFormElement) || form.dataset.zbNotifyBound === 'true') return;
      form.dataset.zbNotifyBound = 'true';
      form.addEventListener('submit', (event) => {
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

        try {
          sessionStorage.setItem('zbStockNotifySubmitted', key);
        } catch (_error) {}

        const button = form.querySelector('.zb-stock-notify__submit');
        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
          if (button.dataset.zbSendingText) button.textContent = button.dataset.zbSendingText;
        }

        if (!window.fetch) return;

        event.preventDefault();
        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            Accept: 'text/html',
          },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Stock notification request failed');
            showNotifySuccess(notice);
          })
          .catch(() => {
            if (button instanceof HTMLButtonElement) button.disabled = false;
            HTMLFormElement.prototype.submit.call(form);
          });
      });
    });
  };

  // B21 (s2/22): one 47-48 note per colour (variant-main-picker.liquid); show the checked colour's note, compared by value.
  const syncSizePriceNote = () => {
    const picker = document.querySelector('variant-picker');
    if (!picker) return;
    const notes = Array.from(picker.querySelectorAll('.zb-size-price-note[data-zb-note-color]'));
    if (!notes.length) return;
    const colorFieldset = fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    if (!colorFieldset) return;
    const color = selectedValue(colorFieldset);
    if (!color) return;
    notes.forEach((note) => {
      const hide = note.getAttribute('data-zb-note-color') !== color;
      if (note.hidden !== hide) note.hidden = hide;
    });
  };

  // s2/26 (B24, B25, plan §2.5 caller): the two sold-out lists in the main form component's [data-zb-size-alts] box.
  // Once a size is chosen (data-zb-size-chosen="true") and the stock entry of the checked colour + size is not in stock
  // (continue_selling / stop_selling), sections/zerbay-size-alternatives.liquid is fetched for that variant with the
  // Section Rendering API (data-product-url carries the /en prefix) and its content is inserted (shown once its card
  // sheet has loaded); in stock, no size or a failed fetch: empty and hidden. One request per variant (in-memory cache;
  // a newer pick aborts the older request; a response for another variant is not shown). Called from syncStockUi, so every write is guarded (no observer loop).
  // No text is written here: headings and cards come from the section.
  const zbAlts = { cache: {}, shown: null, loading: null, failed: null, controller: null, fill: 0 };
  const zbAltsBox = () => {
    const form = zbMainForm();
    return form ? form.querySelector('[data-zb-size-alts]') : null;
  };
  const zbAltsClear = (box) => {
    if (zbAlts.controller) zbAlts.controller.abort();
    zbAlts.controller = null;
    zbAlts.loading = null;
    zbAlts.shown = null;
    if (!box) return;
    if (box.firstChild) box.innerHTML = '';
    if (!box.hidden) box.hidden = true;
    if (box.getAttribute('aria-busy') !== 'false') box.setAttribute('aria-busy', 'false');
  };
  const zbAltsFill = (box, id, html) => {
    zbAlts.fill += 1;
    const token = zbAlts.fill;
    zbAlts.shown = id;
    zbAlts.loading = null;
    zbAlts.controller = null;
    if (!html) {
      if (box.firstChild) box.innerHTML = '';
      box.hidden = true;
      box.setAttribute('aria-busy', 'false');
      return;
    }
    box.innerHTML = html;
    // the request is done; while the box waits for its styles it is hidden, so it is not busy for assistive tech
    box.setAttribute('aria-busy', 'false');
    // the section links custom-collection.css (the card styles): the box is revealed only once that sheet has loaded,
    // so the cards never show unstyled. A sheet that fails or never arrives leaves the box hidden: the lists are a
    // hint, and no lists beat half-styled cards (s2/43: no error or timer reveal; normal loads are unchanged)
    const link = box.querySelector('link[rel="stylesheet"]');
    const reveal = () => {
      // only the latest fill reveals (the load of an earlier fill's sheet must not unhide a newer, unstyled one)
      if (zbAlts.fill !== token || zbAlts.shown !== id || !box.isConnected) return;
      box.hidden = false;
      box.setAttribute('aria-busy', 'false');
    };
    if (link && !link.sheet) {
      if (!box.hidden) box.hidden = true;
      link.addEventListener('load', reveal, { once: true });
    } else {
      reveal();
    }
  };
  const zbAltsRead = (id, text) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const wrap = doc.getElementById('shopify-section-zerbay-size-alternatives') || doc.body;
    const lists = wrap ? wrap.querySelector('.zb-alt') : null;
    if (!lists || lists.getAttribute('data-zb-alt-variant') !== id) return '';
    wrap.querySelectorAll('script').forEach((node) => node.remove());
    return wrap.innerHTML.trim();
  };
  // the stock-map id of the chosen size when it is not in stock, else null
  const zbAltsWanted = () => {
    const form = zbMainForm();
    if (!form || form.getAttribute('data-zb-size-chosen') !== 'true') return null;
    const match = findSelectedStockEntry();
    const status = match && match.entry ? match.entry.s : '';
    return status === 'continue_selling' || status === 'stop_selling' ? String(match.id) : null;
  };
  const syncSizeAlternatives = () => {
    const box = zbAltsBox();
    if (!box || !window.fetch || !window.AbortController) return;
    const form = zbMainForm();
    const id = zbAltsWanted();
    if (id !== zbAlts.failed) zbAlts.failed = null;
    if (!id) {
      if (zbAlts.shown || zbAlts.loading || box.firstChild || !box.hidden) zbAltsClear(box);
      return;
    }
    const cached = Object.prototype.hasOwnProperty.call(zbAlts.cache, id);
    // a box replaced by a re-render comes back empty: fill it again from the cache
    if (id === zbAlts.shown && cached && zbAlts.cache[id] && !box.firstChild) {
      zbAltsFill(box, id, zbAlts.cache[id]);
      return;
    }
    // a request that failed is not repeated by the many later syncs of the same pick (a new pick tries again)
    if (id === zbAlts.shown || id === zbAlts.loading || id === zbAlts.failed) return;
    if (cached) {
      zbAltsClear(box);
      zbAltsFill(box, id, zbAlts.cache[id]);
      return;
    }
    const base = form.dataset.productUrl;
    if (!base) {
      zbAltsClear(box);
      return;
    }
    zbAltsClear(box);
    zbAlts.loading = id;
    box.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    zbAlts.controller = controller;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('variant', id);
    url.searchParams.set('section_id', 'zerbay-size-alternatives');
    fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('zb-alts-status');
        return response.text();
      })
      .then((text) => {
        const html = zbAltsRead(id, text);
        zbAlts.cache[id] = html;
        // a response for a variant that is no longer the chosen one is not shown
        if (zbAlts.controller !== controller || zbAlts.loading !== id) return;
        const current = zbAltsBox();
        if (!current || zbAltsWanted() !== id) {
          zbAltsClear(current);
          return;
        }
        zbAltsFill(current, id, html);
      })
      .catch(() => {
        if (zbAlts.controller !== controller) return;
        zbAltsClear(zbAltsBox());
        zbAlts.failed = id;
      });
  };

  // back from the page cache: a lists request that failed before may be tried again
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    zbAlts.failed = null;
    try {
      syncSizeAlternatives();
    } catch (_error) {}
  });

  const syncStockUi = () => {
    syncPurchaseShippingText();
    syncStickyAvailability();
    syncSizeStockUi();
    syncSizePriceNote();
    bindNotifyFeedback();
    try {
      syncSizeAlternatives();
    } catch (_error) {}
  };

  // ---- Instant buy-button updates -------------------------------------------------
  // The buy button is server-rendered (blocks/buy-buttons.liquid) and normally only
  // reflects a new variant after Horizon re-fetches the section (~1-2s of lag). The
  // snippet `zerbay-variant-stock-map` embeds every variant's purchase state as JSON,
  // so we can update the button the instant a size/colour changes. The background
  // fetch still runs and confirms the same result a moment later.

  const ensureStockMap = () => {
    if (state.stockMap) return state.stockMap;
    const el = document.querySelector('script[data-zb-variant-stock]');
    if (!el) return null;
    try {
      state.stockMap = JSON.parse(el.textContent || '{}');
    } catch (_error) {
      state.stockMap = null;
    }
    return state.stockMap;
  };

  const ensureAddToCartIcon = () => {
    if (state.addToCartIconHtml) return;
    const icon = document.querySelector('add-to-cart-component .add-to-cart-icon');
    if (icon instanceof HTMLElement) state.addToCartIconHtml = icon.outerHTML;
  };

  const findSelectedStockEntry = () => {
    const map = ensureStockMap();
    if (!map) return null;

    const picker = document.querySelector('variant-picker');
    if (!picker) return null;

    const colorFieldset = fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    const sizeFieldset = fieldsetByOption(picker, state.sizeOption, SIZE_WORDS);
    const selectedColor = zbSelectedColor(colorFieldset);
    const selectedSize = sizeFieldset ? normalize(selectedValue(sizeFieldset)) : null;
    const colorIndex = zbColorIndex();
    const sizeIndex = zbSizeIndex();
    // Nothing is selected without a checked size (and, with a colour option, a known colour).
    if (!selectedSize || (colorIndex >= 0 && !selectedColor)) return null;

    let match = null;
    Object.keys(map).some((id) => {
      const entry = map[id];
      const options = entry && entry.o ? entry.o : [];
      const colorOk = colorIndex < 0 || normalize(options[colorIndex]) === selectedColor;
      const sizeOk = sizeIndex < 0 || normalize(options[sizeIndex]) === selectedSize;
      if (colorOk && sizeOk) {
        match = { id, entry };
        return true;
      }
      return false;
    });
    return match;
  };

  const updateAddToCartButton = (canAddToCart, text) => {
    const button = document.querySelector(
      'add-to-cart-component button[ref="addToCartButton"], add-to-cart-component button.add-to-cart-button'
    );
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = !canAddToCart;

    const textNode =
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content');
    if (textNode && typeof text === 'string' && text) textNode.textContent = text;

    const textWrap = button.querySelector('.add-to-cart-text');
    const icon = button.querySelector('.add-to-cart-icon');
    if (canAddToCart) {
      if (!icon && state.addToCartIconHtml && textWrap) {
        textWrap.insertAdjacentHTML('afterbegin', state.addToCartIconHtml);
      }
    } else if (icon) {
      icon.remove();
    }
  };

  // Horizon's native data-option-available (which drives the faded/disabled size
  // pills) is only refreshed by its morph on variant change - and the parity layer
  // patches that morph out to avoid re-rendering the product DOM. So after a color
  // change the size pills keep the *previous* color's availability. Recompute each
  // non-selected size's availability straight from the per-variant stock map for the
  // currently selected color. The metafield-driven stock status decides selectability:
  // in_stock and continue_selling stay selectable; only stop_selling is unavailable.
  const syncSizeAvailabilityFromMap = () => {
    const map = ensureStockMap();
    if (!map) return;
    const picker = document.querySelector('variant-picker');
    if (!picker) return;
    const colorFieldset = fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    const sizeFieldset = fieldsetByOption(picker, state.sizeOption, SIZE_WORDS);
    if (!sizeFieldset) return;
    const sizeIndex = zbSizeIndex();
    const colorIndex = zbColorIndex();
    if (sizeIndex < 0) return;
    const selectedColor = zbSelectedColor(colorFieldset);
    // s2/22: the live product picker keeps every size selectable for assistive tech (no aria-disabled); a size that
    // is not in stock in the checked colour is described by the sold-out word instead. Other pickers keep today's code.
    const isLivePicker = picker.hasAttribute('data-zb-pdp-live');
    let soldOutText = null;
    if (isLivePicker) {
      soldOutText = sizeFieldset.querySelector('[data-zb-soldout-text]');
      const soldOutId = `zbSoldOutText-${picker.dataset.sectionId || ''}`;
      if (soldOutText && soldOutText.id !== soldOutId) soldOutText.id = soldOutId;
    }

    const sizeStatusPriority = {
      in_stock: 3,
      continue_selling: 2,
      stop_selling: 1,
    };
    const stateBySize = {};

    Object.keys(map).forEach((id) => {
      const entry = map[id];
      const options = entry && entry.o ? entry.o : [];
      if (colorIndex >= 0 && selectedColor != null && normalize(options[colorIndex]) !== selectedColor) return;
      const sizeVal = normalize(options[sizeIndex]);
      if (sizeVal == null) return;
      const status = entry.s || (entry.a ? 'in_stock' : 'stop_selling');
      const current = stateBySize[sizeVal];
      if (!current || (sizeStatusPriority[status] || 0) > (sizeStatusPriority[current.status] || 0)) {
        stateBySize[sizeVal] = {
          status,
          addable: !!entry.a,
        };
      }
    });

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const sizeVal = normalize(input.value);
      if (!(sizeVal in stateBySize)) return;
      const sizeState = stateBySize[sizeVal];
      const status = sizeState.status || 'stop_selling';
      const isAddable = !!sizeState.addable;
      const isContinueSelling = status === 'continue_selling';
      const isStopSelling = status === 'stop_selling';
      const isSelectable = isAddable || isContinueSelling;
      const label = input.closest('label');

      input.dataset.zbStockStatus = status;
      input.dataset.optionAvailable = isSelectable ? 'true' : 'false';
      if (isLivePicker) {
        if (input.hasAttribute('aria-disabled')) input.removeAttribute('aria-disabled');
        if (soldOutText && status !== 'in_stock') {
          if (input.getAttribute('aria-describedby') !== soldOutText.id) input.setAttribute('aria-describedby', soldOutText.id);
        } else if (input.hasAttribute('aria-describedby')) {
          input.removeAttribute('aria-describedby');
        }
      } else if (isSelectable) input.removeAttribute('aria-disabled');
      else input.setAttribute('aria-disabled', 'true');

      if (label) {
        label.dataset.zbStockStatus = status;
        label.dataset.zbSelectable = isSelectable ? 'true' : 'false';
        label.classList.toggle('zb-is-continue-selling', status === 'continue_selling');
        label.classList.toggle('zb-is-stop-selling', isStopSelling);
        label.classList.toggle('zb-is-unavailable', isStopSelling);
      }
    });
  };

  const fastVariantStockUpdate = () => {
    ensureAddToCartIcon();
    syncSizeAvailabilityFromMap();
    const match = findSelectedStockEntry();
    if (!match) {
      syncSizeStockUi();
      return;
    }

    const form = currentProductForm();
    if (form) {
      form.dataset.zbStockStatus = match.entry.s || '';
      form.dataset.zbCanAddToCart = match.entry.a ? 'true' : 'false';
      form.dataset.zbVariantId = match.id;
    }

    updateAddToCartButton(!!match.entry.a, match.entry.t);
    syncStockUi();
  };

  const bindFilteredControl = (button, slideshow, direction) => {
    if (!button || button.dataset.zbFilteredBound) return;
    button.dataset.zbFilteredBound = 'true';
    button.addEventListener(
      'click',
      (event) => {
        const visibleIndexes = (slideshow.dataset.zbVisibleIndexes || '')
          .split(',')
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value));

        if (!visibleIndexes.length || typeof slideshow.select !== 'function') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const current = Number.isFinite(slideshow.current) ? slideshow.current : visibleIndexes[0];
        const currentPosition = visibleIndexes.indexOf(current);
        const position = currentPosition >= 0 ? currentPosition : 0;
        const nextPosition = (position + direction + visibleIndexes.length) % visibleIndexes.length;
        slideshow.select(visibleIndexes[nextPosition], event, { animate: true });
      },
      true
    );
  };

  const syncGalleryControls = (gallery, visibleIndexes) => {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || !visibleIndexes.length) return;

    slideshow.dataset.zbVisibleIndexes = visibleIndexes.join(',');

    const controls =
      gallery.querySelector('slideshow-controls.media-gallery__mobile-controls') || gallery.querySelector('slideshow-controls');
    const dots = controls ? Array.from(controls.querySelectorAll('.slideshow-controls__dots li')) : [];
    dots.forEach((item, index) => {
      const show = visibleIndexes.includes(index);
      item.hidden = !show;
      item.classList.toggle('zb-product-media-hidden', !show);

      const button = item.querySelector('button');
      if (!button) return;
      button.tabIndex = show ? 0 : -1;
      button.setAttribute('aria-selected', String(show && index === visibleIndexes[0]));
      if (show) button.removeAttribute('aria-hidden');
      else button.setAttribute('aria-hidden', 'true');
    });

    bindFilteredControl(controls?.querySelector('.slideshow-control--previous'), slideshow, -1);
    bindFilteredControl(controls?.querySelector('.slideshow-control--next'), slideshow, 1);

    window.requestAnimationFrame(() => {
      if (!slideshow.isConnected || typeof slideshow.select !== 'function') return;
      if (!visibleIndexes.includes(slideshow.current)) {
        slideshow.select(visibleIndexes[0], undefined, { animate: false });
      }
    });
  };

  const filterGallery = () => {
    const picker = document.querySelector('variant-picker');
    const colorFieldset = picker && fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    const group = groupForColor(state.previewColor || selectedValue(colorFieldset));
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !group || !group.galleryImages.length) return;

    group.galleryImages.slice(0, 4).forEach((image) => preloadImage(image.src));

    const allowed = new Map(group.galleryImages.map((image, index) => [imageKey(image.src), index]));
    const slideshowSlides = Array.from(gallery.querySelectorAll('slideshow-slide.product-media-container'));
    const gridSlides = Array.from(gallery.querySelectorAll('.media-gallery__grid > .product-media-container'));
    const slides =
      slideshowSlides.length || gridSlides.length
        ? slideshowSlides.concat(gridSlides)
        : Array.from(gallery.querySelectorAll('.product-media-container'));
    const visibleIndexes = [];
    let visible = 0;

    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      const key = imageKey(img?.currentSrc || img?.src || img?.getAttribute('src'));
      const show = allowed.has(key);
      const order = allowed.get(key) ?? 0;
      const slideshowIndex = slideshowSlides.indexOf(slide);

      if (slide.hidden !== !show) slide.hidden = !show;
      slide.classList.toggle('zb-product-media-hidden', !show);
      slide.classList.toggle('zb-product-media-visible', show);
      slide.classList.toggle('zb-media-wide', show && order % 3 === 0);
      if (show) slide.style.order = String(order);
      else slide.style.removeProperty('order');
      const ariaHidden = show && visible === 0 ? 'false' : 'true';
      if (slide.getAttribute('aria-hidden') !== ariaHidden) slide.setAttribute('aria-hidden', ariaHidden);

      if (show && slideshowIndex >= 0) visibleIndexes.push(slideshowIndex);
      if (show) visible += 1;
    });

    if (!visible) {
      visibleIndexes.length = 0;
      slides.forEach((slide, index) => {
        slide.hidden = false;
        slide.classList.remove('zb-product-media-hidden');
        slide.classList.add('zb-product-media-visible');
        slide.style.removeProperty('order');
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });
      slideshowSlides.forEach((_slide, index) => visibleIndexes.push(index));
      visible = slides.length;
    }

    syncGalleryControls(gallery, visibleIndexes);
    gallery.classList.add('zb-gallery-filtered');
    if (gallery.dataset.zbVisibleMedia !== String(visible)) {
      gallery.dataset.zbVisibleMedia = String(visible);
    }
  };

  const enhance = async () => {
    document.body.classList.add('zb-product-parity');
    await loadProduct();

    decorateHeader();
    decorateLayout();
    decoratePicker();
    filterGallery();
    syncStockUi();
    // s2/23: an open size guide follows the selected colour and size (equality-guarded writes, no observer loop).
    const sizeGuide = document.getElementById(SIZE_GUIDE_ID);
    if (sizeGuide && sizeGuide.open) fillSizeGuide(sizeGuide);
    syncSizeGuideLock();
    document.body.classList.add('zb-product-parity-ready');
    document.body.classList.remove('zb-product-preflight');
  };

  const scheduleEnhance = (delay) => {
    window.clearTimeout(state.queued);
    state.queued = window.setTimeout(enhance, delay || 0);
  };

  const syncStockNotice = (html, productId) => {
    const currentForm =
      document.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
      document.querySelector('product-form-component');
    const newForm =
      html.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
      html.querySelector('product-form-component');
    if (!currentForm || !newForm) return;

    ['zbStockStatus', 'zbCanAddToCart', 'zbVariantInventory', 'zbVariantId'].forEach((key) => {
      if (newForm.dataset[key] !== undefined) currentForm.dataset[key] = newForm.dataset[key];
      else delete currentForm.dataset[key];
    });

    const currentNotice = currentForm.querySelector('[data-zb-stock-notify]');
    const newNotice = newForm.querySelector('[data-zb-stock-notify]');
    if (currentNotice && newNotice) {
      currentNotice.replaceWith(newNotice.cloneNode(true));
    } else if (currentNotice) {
      currentNotice.remove();
    } else if (newNotice) {
      // s2/26: a new Notify block goes before the sold-out lists box, so the lists stay under it
      const alts = currentForm.querySelector('[data-zb-size-alts]');
      if (alts) alts.before(newNotice.cloneNode(true));
      else currentForm.append(newNotice.cloneNode(true));
    }

    syncStockUi();
  };

  // s2/22 terminal path of a variant fetch that failed twice: no variant:update is dispatched. The controls may follow
  // the stock entry of the checked colour + size only if that entry, the live component's hidden id and its
  // data-zb-variant-id name the same variant; otherwise the component is set not addable first, so the controls, the
  // sticky-bar host and #processAddToCart all refuse. Then product-form.js ends the pending change and drops queued
  // presses (only once the component is upgraded; before that no pending flag exists). The id keeps the pre-filled
  // chosen variant; price and Notify keep their last fetched content until the next successful pick.
  const zbEndFailedVariantChange = (picker) => {
    const section = picker.closest('.shopify-section');
    const form = (section && section.querySelector('product-form-component')) || currentProductForm();
    if (!(form instanceof HTMLElement)) return;
    const idInput = form.querySelector('input[name="id"]');
    const match = findSelectedStockEntry();
    const allowAdd = Boolean(
      match && idInput && String(idInput.value) === String(match.id) && String(form.dataset.zbVariantId || '') === String(match.id)
    );
    if (allowAdd) {
      form.dataset.zbStockStatus = match.entry.s || '';
      form.dataset.zbCanAddToCart = match.entry.a ? 'true' : 'false';
    } else {
      form.dataset.zbCanAddToCart = 'false';
    }
    if (typeof form.zbEndFailedVariantChange === 'function') form.zbEndFailedVariantChange(allowAdd);
    syncStockUi();
  };

  const patchVariantPickerRefresh = () => {
    if (!window.customElements || !customElements.whenDefined) return;

    customElements.whenDefined('variant-picker').then(() => {
      const VariantPicker = customElements.get('variant-picker');
      if (!VariantPicker || VariantPicker.prototype.zbProductParityPatched) return;

      // Keep Horizon's background variant fetch, but stop it morphing the picker/gallery into the base UI.
      const originalFetchUpdatedSection = VariantPicker.prototype.fetchUpdatedSection;
      VariantPicker.prototype.fetchUpdatedSection = function (requestUrl, morphElementSelector) {
        const shouldKeepProductDom =
          document.body.classList.contains('template-product') &&
          this.matches('variant-picker[data-template-product-match="true"]') &&
          !morphElementSelector;

        if (!shouldKeepProductDom) {
          return originalFetchUpdatedSection.call(this, requestUrl, morphElementSelector);
        }

        this.zbAbortController?.abort();
        window.clearTimeout(this.zbRetryTimer);
        const controller = new AbortController();
        this.zbAbortController = controller;

        // s2/22 (Codex r4 M3 / r5): the response is validated before anything is written - OK status, the picker's
        // variant JSON present, parsed, a non-null object with an id. A failure (network, non-OK, no / malformed /
        // null / id-less variant JSON) writes nothing and starts one retry of the same request after 1 s (aborted like
        // any request when a newer pick starts one; an aborted retry ends silently). If the retry fails too, the
        // terminal path runs (zbEndFailedVariantChange above). Horizon's own fetch + morph is no longer called for the live
        // product picker.
        const attempt = (isRetry) => {
          fetch(requestUrl, { signal: controller.signal })
            .then((response) => {
              if (!response.ok) throw new Error('zb-variant-fetch-status');
              return response.text();
            })
            .then((responseText) => {
              if (this.zbAbortController !== controller) return;

              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const variantJson = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
              let variant = null;
              try {
                variant = variantJson ? JSON.parse(variantJson) : null;
              } catch (_error) {
                variant = null;
              }
              if (!variant || typeof variant !== 'object' || !variant.id) throw new Error('zb-variant-fetch-json');

              html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

              const newVariantPicker = html.querySelector('variant-picker');
              if (newVariantPicker instanceof HTMLElement) {
                this.dataset.productId = newVariantPicker.dataset.productId || this.dataset.productId;
                this.dataset.productUrl = newVariantPicker.dataset.productUrl || this.dataset.productUrl;
              }

              const currentVariantJson = this.querySelector('script[type="application/json"]');
              if (currentVariantJson) currentVariantJson.textContent = variantJson;

              const productId = this.dataset.productId || '';
              try {
                syncStockNotice(html, productId);
              } catch (_error) {}

              html.querySelectorAll('media-gallery, variant-picker').forEach((element) => element.remove());

              let sourceId = '';
              try {
                sourceId = this.selectedOptionId || '';
              } catch (_error) {
                sourceId = '';
              }

              this.dispatchEvent(
                new CustomEvent('variant:update', {
                  bubbles: true,
                  detail: {
                    resource: variant,
                    sourceId,
                    data: {
                      html,
                      productId,
                      newProduct: undefined,
                    },
                  },
                })
              );

              scheduleEnhance(0);
            })
            .catch((error) => {
              if (error && error.name === 'AbortError') return;
              if (this.zbAbortController !== controller) return;
              if (!isRetry) {
                this.zbRetryTimer = window.setTimeout(() => {
                  if (this.zbAbortController === controller) attempt(true);
                }, 1000);
                return;
              }
              // The terminal path belongs to the live product picker only; any other picker that takes this branch (e.g. a
              // quick-add copy of a product page, which carries the server's data-template-product-match) keeps Horizon's
              // own fetch + morph as before.
              if (this.hasAttribute('data-zb-pdp-live')) zbEndFailedVariantChange(this);
              else originalFetchUpdatedSection.call(this, requestUrl, morphElementSelector);
            });
        };
        attempt(false);
      };

      VariantPicker.prototype.zbProductParityPatched = true;
    });
  };

  const previewColorFromTarget = (target) => {
    if (!canPreviewOnHover() || !target?.closest) return;

    const label = target.closest('.zb-product-color-fieldset label');
    if (!label) return;

    const fieldset = label.closest('.zb-product-color-fieldset');
    const input = label.querySelector('input[type="radio"]');
    if (!fieldset || !input) return;

    const colorIndex = colorIndexForInput(input, fieldset);
    setLegend(fieldset, 'Color', input.value);
    showPreviewColor(input.value, colorIndex);
  };

  patchVariantPickerRefresh();

  document.addEventListener('mouseover', (event) => previewColorFromTarget(event.target), true);

  document.addEventListener('focusin', (event) => previewColorFromTarget(event.target), true);

  document.addEventListener(
    'mouseout',
    (event) => {
      if (!canPreviewOnHover() || !event.target?.closest) return;

      const fieldset = event.target.closest('.zb-product-color-fieldset');
      if (!fieldset || fieldset.contains(event.relatedTarget)) return;

      setLegend(fieldset, 'Color', selectedValue(fieldset));
      clearPreviewColor(fieldset);
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (!event.target.closest || !event.target.closest('variant-picker')) return;

    // Reflect the new variant's stock state on the button immediately, before the fetch.
    fastVariantStockUpdate();

    const picker = event.target.closest('variant-picker');
    const colorFieldset = picker && fieldsetByOption(picker, state.colorOption, COLOR_WORDS);
    const isColorChange = colorFieldset && colorFieldset.contains(event.target);

    if (isColorChange) {
      commitPreviewColor(event.target, colorFieldset);
      window.setTimeout(() => {
        if (normalize(state.committingColor) === normalize(selectedValue(colorFieldset))) {
          state.previewColor = selectedValue(colorFieldset);
          state.committingColor = '';
          filterGallery();
        }
      }, 500);
    } else {
      state.previewColor = '';
      state.committingColor = '';
    }

    scheduleEnhance(0);
    window.setTimeout(() => scheduleEnhance(0), 120);
    window.setTimeout(() => scheduleEnhance(0), 320);
    window.setTimeout(() => scheduleEnhance(0), 700);
  });

  document.addEventListener('variant:update', () => {
    syncStockUi();
    window.setTimeout(syncStockUi, 0);
    window.setTimeout(syncStockUi, 120);
  });

  const observer = new MutationObserver(() => scheduleEnhance(50));
  observer.observe(document.body, { childList: true, subtree: true });

  ensureStockMap();
  ensureAddToCartIcon();
  scheduleEnhance();
})();
